import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import Icons from '@aurora/shared-client/icons';
import type { TagPagesAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { MessageConstraints } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent, EndUserPathParams } from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { buildMessagesConstraintsForNode } from '../../../helpers/nodes/NodeHelper/NodeHelper';
import type {
  MessagesCountForTagQuery,
  MessagesCountForTagQueryVariables
} from '../../../types/graphql-types';
import type { WidgetFC, WidgetProps } from '../../common/Widget/types';
import useTranslation from '../../useTranslation';
import messagesCountForTagQuery from './MessagesCountForTag.query.graphql';
import localStyles from './TagsHeaderWidget.module.css';

/**
 * Header widget for the tags page
 *
 * @constructor
 *
 * @author Manasvini Arul
 */
const TagsHeaderWidget: WidgetFC<WidgetProps> = () => {
  const { router, loading: loadingRoutes } = useEndUserRoutes();
  const tagName = router.getPathParam<TagPagesAndParams>(EndUserPathParams.TAG_NAME);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.TAGS_HEADER_WIDGET
  );
  const cx = useClassNameMapper(localStyles);
  const { contextNode } = useContext(AppContext);
  const constraints: MessageConstraints = {
    tagsText: { eq: tagName },
    ...buildMessagesConstraintsForNode(contextNode)
  };

  const queryResult = useQueryWithTracing<
    MessagesCountForTagQuery,
    MessagesCountForTagQueryVariables
  >(module, messagesCountForTagQuery, {
    variables: {
      constraints
    }
  });

  if (textLoading || loadingRoutes || queryResult.loading) {
    return null;
  }

  return (
    <header className={cx('lia-header')}>
      <Icon
        icon={Icons.ContentTagIcon}
        size={IconSize.PX_80}
        useFrame
        className={cx('lia-g-mr-20')}
        color={IconColor.GRAY_600}
      />
      <div>
        <h1 className={cx('lia-title')}>{formatMessage('tag', { tagName })}</h1>
        <span className={cx('lia-topics-count')}>
          {formatMessage('topicsCount', { count: queryResult.data.messages.totalCount })}
        </span>
      </div>
    </header>
  );
};
export default TagsHeaderWidget;
